import React, { useState } from 'react';
import {
  Database,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  RotateCcw,
  Clock,
  HardDrive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QAErrorAlert } from './QAErrorAlert';
import { formatDate } from './qaUtils';
import type {
  VectorHealthAudit,
  VectorDocumentHealth,
  StaleDocumentsResponse,
  StaleDocument,
} from '@/services/kbService';

export interface QAVectorHealthTabProps {
  audit: VectorHealthAudit | null;
  staleData: StaleDocumentsResponse | null;
  loading: boolean;
  error: string | null;
  reindexing: boolean;
  onRefresh: () => void;
  onReindexDocument: (documentId: string) => void;
  onBatchReindex: (documentIds: string[]) => void;
}

const statusConfig = {
  healthy: { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
  warning: { color: 'bg-amber-500/15 text-amber-500 border-amber-500/30', icon: AlertTriangle },
  critical: { color: 'bg-destructive/15 text-red-400 border-destructive/30', icon: XCircle },
};

export const QAVectorHealthTab: React.FC<QAVectorHealthTabProps> = ({
  audit,
  staleData,
  loading,
  error,
  reindexing,
  onRefresh,
  onReindexDocument,
  onBatchReindex,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleSelect = (docId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(docId);
      else next.delete(docId);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && audit) {
      const allIds = new Set([
        ...audit.document_health.filter((d) => d.status !== 'healthy').map((d) => d.document_id),
        ...(staleData?.stale_documents.map((d) => d.document_id) || []),
      ]);
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBatchReindex = () => {
    if (selectedIds.size > 0) {
      onBatchReindex(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner message="Loading vector health..." fullScreen={false} />
      </div>
    );
  }

  if (error) {
    return <QAErrorAlert message="Vector Health Error" error={error} onRetry={onRefresh} />;
  }

  if (!audit) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Database className="h-12 w-12 mx-auto mb-4 opacity-30" />
        <p>Click refresh to load vector health data</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onRefresh}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Load Vector Health
        </Button>
      </div>
    );
  }

  const prodStats = audit.collection_stats.production;
  const stagingStats = audit.collection_stats.staging;
  const unhealthyDocs = audit.document_health.filter((d) => d.status !== 'healthy');
  const hasStale = staleData && staleData.total_stale > 0;

  return (
    <div className="space-y-6">
      {/* Header + Actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          <h3 className="text-sm sm:text-base font-semibold">Vector Index Health</h3>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              onClick={handleBatchReindex}
              disabled={reindexing}
            >
              {reindexing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
              Re-index {selectedIds.size} Selected
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Collection Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Production Vectors</p>
                {/* points_count is the live truth; indexed_vectors_count lags
                    behind deletions until Qdrant's optimizer vacuums segments */}
                <p className="text-xl font-bold">{prodStats?.points_count?.toLocaleString() ?? 'N/A'}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {prodStats?.points_count
                    ? `${prodStats.indexed_vectors_count?.toLocaleString() ?? 0} indexed | `
                    : ''}
                  Status: {prodStats?.status ?? 'unknown'}
                </p>
              </div>
              <div className="p-2 rounded-full bg-primary/15">
                <HardDrive className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Health Summary Bar */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          {audit.documents_healthy} Healthy
        </Badge>
        <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
          <AlertTriangle className="mr-1 h-3 w-3" />
          {audit.documents_warning} Warning
        </Badge>
        <Badge variant="outline" className="bg-destructive/10 text-red-400 border-destructive/30">
          <XCircle className="mr-1 h-3 w-3" />
          {audit.documents_critical} Critical
        </Badge>
        {audit.orphaned_vector_documents.length > 0 && (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
            <AlertTriangle className="mr-1 h-3 w-3" />
            {audit.orphaned_vector_documents.length} Orphaned
          </Badge>
        )}
        {hasStale && (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
            <Clock className="mr-1 h-3 w-3" />
            {staleData.total_stale} Stale
          </Badge>
        )}
      </div>

      {/* Orphaned Vectors Warning */}
      {audit.orphaned_vector_documents.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-4 w-4" />
              Orphaned Vectors Detected
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-amber-500 mb-2">
              These document IDs exist in Qdrant but not in Frappe. They may consume storage without serving queries.
            </p>
            <div className="flex flex-wrap gap-1">
              {audit.orphaned_vector_documents.slice(0, 10).map((docId) => (
                <Badge key={docId} variant="outline" className="text-xs font-mono">
                  {docId}
                </Badge>
              ))}
              {audit.orphaned_vector_documents.length > 10 && (
                <Badge variant="outline" className="text-xs">
                  +{audit.orphaned_vector_documents.length - 10} more
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stale Documents Section */}
      {hasStale && (
        <Card className="border-amber-500/30 bg-amber-500/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-500">
              <Clock className="h-4 w-4" />
              Stale Documents - Re-indexing Recommended
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-amber-500 mb-3">
              These documents had QA actions (corrections, merges, deletions) after their last embedding update.
            </p>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {staleData.stale_documents.map((doc: StaleDocument) => (
                <div key={doc.document_id} className="border border-amber-500/30 rounded-lg bg-card/40 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      className="mt-1"
                      checked={selectedIds.has(doc.document_id)}
                      onCheckedChange={(checked) => handleSelect(doc.document_id, !!checked)}
                    />
                    <span className="text-sm font-medium break-all flex-1" title={doc.filename}>
                      {doc.filename}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <span className="text-[11px] uppercase tracking-wide">Reason: </span>
                    {doc.reason}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <span className="text-[11px] uppercase tracking-wide">Last QA Action: </span>
                    {formatDate(doc.last_qa_action)}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs w-full"
                    onClick={() => onReindexDocument(doc.document_id)}
                    disabled={reindexing}
                  >
                    <RotateCcw className="mr-1 h-3 w-3" />
                    Re-index
                  </Button>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 pr-2 w-8">
                      <Checkbox
                        checked={staleData.stale_documents.every((d) => selectedIds.has(d.document_id))}
                        onCheckedChange={(checked) => {
                          const staleIds = staleData.stale_documents.map((d) => d.document_id);
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            staleIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
                            return next;
                          });
                        }}
                      />
                    </th>
                    <th className="pb-2 pr-4">Document</th>
                    <th className="pb-2 pr-4">Reason</th>
                    <th className="pb-2 pr-4">Last QA Action</th>
                    <th className="pb-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {staleData.stale_documents.map((doc: StaleDocument) => (
                    <tr key={doc.document_id} className="border-b last:border-0">
                      <td className="py-2 pr-2">
                        <Checkbox
                          checked={selectedIds.has(doc.document_id)}
                          onCheckedChange={(checked) => handleSelect(doc.document_id, !!checked)}
                        />
                      </td>
                      <td className="py-2 pr-4">
                        <span className="font-medium" title={doc.filename}>
                          {doc.filename}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">{doc.reason}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{formatDate(doc.last_qa_action)}</td>
                      <td className="py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => onReindexDocument(doc.document_id)}
                          disabled={reindexing}
                        >
                          <RotateCcw className="mr-1 h-3 w-3" />
                          Re-index
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Health Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Document Vector Coverage ({audit.total_documents} documents)
            </span>
            {unhealthyDocs.length > 0 && (
              <Checkbox
                checked={unhealthyDocs.every((d) => selectedIds.has(d.document_id))}
                onCheckedChange={(checked) => handleSelectAll(!!checked)}
              />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {audit.document_health.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No documents found</p>
          ) : (() => {
            const hasUnhealthy = audit.document_health.some((d: VectorDocumentHealth) => d.status !== 'healthy');
            return (
            <>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {audit.document_health.map((doc: VectorDocumentHealth) => {
                const cfg = statusConfig[doc.status];
                const Icon = cfg.icon;
                return (
                  <div key={doc.document_id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        {hasUnhealthy && doc.status !== 'healthy' && (
                          <Checkbox
                            className="mt-1"
                            checked={selectedIds.has(doc.document_id)}
                            onCheckedChange={(checked) => handleSelect(doc.document_id, !!checked)}
                          />
                        )}
                        <span className="text-sm font-medium break-all" title={doc.filename}>
                          {doc.filename}
                        </span>
                      </div>
                      <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${cfg.color}`}>
                        <Icon className="mr-1 h-3 w-3" />
                        {doc.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={doc.coverage_percent} className="h-2 flex-1" />
                      <span className="text-[10px] font-mono w-10 text-right">{doc.coverage_percent}%</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <div className="text-muted-foreground">
                        <span className="font-mono">{doc.actual_vectors}</span>
                        {' / '}
                        <span className="font-mono">{doc.expected_chunks}</span>
                        {' chunks'}
                      </div>
                      {hasUnhealthy && doc.status !== 'healthy' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => onReindexDocument(doc.document_id)}
                          disabled={reindexing}
                        >
                          <RotateCcw className="mr-1 h-3 w-3" />
                          Re-index
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left">
                    {hasUnhealthy && <th className="pb-2 pr-2 w-8"></th>}
                    <th className="pb-2 pr-4">Document</th>
                    <th className="pb-2 pr-4 text-right">Expected</th>
                    <th className="pb-2 pr-4 text-right">Actual</th>
                    <th className="pb-2 pr-4 w-32">Coverage</th>
                    <th className="pb-2 pr-4">Status</th>
                    {hasUnhealthy && <th className="pb-2">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {audit.document_health.map((doc: VectorDocumentHealth) => {
                    const cfg = statusConfig[doc.status];
                    const Icon = cfg.icon;
                    return (
                      <tr key={doc.document_id} className="border-b last:border-0">
                        {hasUnhealthy && (
                          <td className="py-2 pr-2">
                            {doc.status !== 'healthy' && (
                              <Checkbox
                                checked={selectedIds.has(doc.document_id)}
                                onCheckedChange={(checked) => handleSelect(doc.document_id, !!checked)}
                              />
                            )}
                          </td>
                        )}
                        <td className="py-2 pr-4">
                          <span className="font-medium" title={doc.filename}>
                            {doc.filename}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right font-mono">{doc.expected_chunks}</td>
                        <td className="py-2 pr-4 text-right font-mono">{doc.actual_vectors}</td>
                        <td className="py-2 pr-4">
                          <div className="flex items-center gap-2">
                            <Progress value={doc.coverage_percent} className="h-2 flex-1" />
                            <span className="text-[10px] font-mono w-10 text-right">{doc.coverage_percent}%</span>
                          </div>
                        </td>
                        <td className="py-2 pr-4">
                          <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>
                            <Icon className="mr-1 h-3 w-3" />
                            {doc.status}
                          </Badge>
                        </td>
                        {hasUnhealthy && (
                          <td className="py-2">
                            {doc.status !== 'healthy' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => onReindexDocument(doc.document_id)}
                                disabled={reindexing}
                              >
                                <RotateCcw className="mr-1 h-3 w-3" />
                                Re-index
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
};
